import { endOfDay, formatISO, parseISO, startOfDay } from 'date-fns';

/** yyyy-MM-dd → start-of-day ISO-8601 with local timezone offset (e.g. +08:00). */
export function toStartOfDayISO(yyyyMmDd: string): string {
  return formatISO(startOfDay(parseISO(yyyyMmDd)));
}

/** yyyy-MM-dd → end-of-day ISO-8601 with local timezone offset. */
export function toEndOfDayISO(yyyyMmDd: string): string {
  return formatISO(endOfDay(parseISO(yyyyMmDd)));
}

export const getChainStorePayPlatformUrl = (
  paymentId: string | null | undefined,
) => {
  if (!paymentId) return null;
  const vite_base_url = import.meta.env.VITE_BASE_URL;

  return `${vite_base_url}/invoices/${paymentId}/edit`;
};

export const LIST_PAGE_SIZE = 1000;

type PagedResponse<T> = {
  data: T[];
  extra: {
    totalElement: number;
    totalPage: number;
  };
};

type PagedParams = {
  page?: number;
  pageSize?: number;
};

// 列表的搜尋與分頁都在前端處理，所以要把所有頁面抓完再合併
// 僅適用 cockpit/auto-pay list API 的分頁格式（page 從 1 開始、回傳 extra.totalPage）
export async function fetchAllPages<T, P extends PagedParams>(
  fetchPage: (params: P) => Promise<PagedResponse<T>>,
  params: P,
  pageSize: number = LIST_PAGE_SIZE,
): Promise<PagedResponse<T>> {
  const first = await fetchPage({ ...params, page: 1, pageSize });
  const totalPage = first.extra?.totalPage;

  if (!totalPage) {
    // 沒有 totalPage 可依靠時，逐頁往下抓到不滿頁為止。
    const data = [...first.data];
    let page = 1;
    while (data.length && data.length % pageSize === 0) {
      page += 1;
      const next = await fetchPage({ ...params, page, pageSize });
      if (!next.data.length) break;
      data.push(...next.data);
    }
    return { ...first, data };
  }

  if (totalPage <= 1) return first;

  const restPages = await Promise.all(
    Array.from({ length: totalPage - 1 }, (_, i) =>
      fetchPage({ ...params, page: i + 2, pageSize }),
    ),
  );

  return {
    ...first,
    data: [...first.data, ...restPages.flatMap((res) => res.data)],
  };
}
